// @flow
import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import { NavigationUtil } from 'utils/NavigationUtil';
import { RouteNames } from 'router';
import { HomeActions } from 'modules/home/actions';
import { HomeSelector } from 'modules/home/selector';
import { PageLoader } from 'components/molecules/pageLoader';
import { ContactUsSection } from 'components/templates/contactUsSection';
import FooterSection from 'components/templates/footer';
import PageTemplate from 'components/templates/pageTemplate';
import { useViewport } from 'components/templates/viewportProvider';
import type { AppState } from 'modules/rootReducer';
import type { Contact, SiteSettings, Vertical } from 'types/common';
import { CmsPagesActions } from 'modules/cmsPages/actions';
import { CmsPagesSelector } from 'modules/cmsPages/selector';
import CraftBeerSection from 'components/templates/craftBeerAndBrewery';
import { GetInTouchModal } from 'components/templates/getInTouchModal';
import { ShareModal } from 'components/templates/shareModal';
import BeerExplorer from 'components/organisms/beerExplorer';
import CraftBeerBreweryBanner from 'components/organisms/craftBeerBreweryBanner';
import { RestaurantActions } from 'modules/restaurant/actions';
import { RestaurantSelector } from 'modules/restaurant/selector';
import { HeaderButtons } from 'components/atoms/headerButtons';
import { FooterSelector } from 'modules/footerLink/selector';
import { FooterActions } from 'modules/footerLink/actions';

type Props = {
  isLoading: boolean,
  verticals: Array<Vertical>,
  verticalId: number | null,
  getVerticals: () => void,
  siteSettings: SiteSettings,
  getSiteSettings: () => void,
  contact: Contact,
  getContactDetails: () => void,
  craftBeerSectionData: any,
};

const CraftBeerPage = (props: Props) => {
  const {
    isLoading,
    getVerticals,
    verticals,
    verticalId,
    getPageData,
    craftBeerSectionData,
    craftBeerBanner,
    getBannerData,
    getSiteSettings,
    siteSettings,
    footerLink,
    getFooterLinkData,
    contact,
    getContactDetails,
    getBeers,
    beerData,
  } = props;

  const { isMobile } = useViewport();
  let [theme, setTheme] = useState(isMobile ? 'dark-theme' : 'light-theme');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  useEffect(() => {
    getBannerData();
    getContactDetails();
    getVerticals();
    getPageData();
    getSiteSettings();
    getBeers();
    getFooterLinkData();
  }, []);

  if (isLoading) {
    return <PageLoader isMobile={isMobile} />;
  }

  if (
    !contact ||
    !siteSettings ||
    !verticals ||
    !craftBeerSectionData ||
    !craftBeerBanner ||
    !beerData ||
    !footerLink
  ) {
    return null;
  }

  // Get in touch
  const onGetInTouchClick = () => {
    setIsModalOpen(true);
  };

  // Share
  const onShareClick = () => {
    setIsShareModalOpen(true);
  };

  const closeShareModal = () => {
    setIsShareModalOpen(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const componentList = [
    ...(!isEmpty(craftBeerBanner)
      ? [
          {
            component: CraftBeerBreweryBanner,
            data: { craftBeerBanner },
            theme: isMobile ? 'dark-theme' : 'light-theme',
            sectionId: 'banner',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Craft Beer & Brewery',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(craftBeerSectionData)
      ? [
          {
            component: CraftBeerSection,
            data: { craftBeerSectionData },
            theme: 'light-theme',
            sectionId: 'brewery',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Craft Beer & Brewery',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(beerData)
      ? [
          {
            component: BeerExplorer,
            data: { beerData },
            theme: 'light-theme',
            sectionId: 'beer-explorer',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Beer Explorer',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    {
      component: ContactUsSection,
      data: {
        contact,
        verticals,
        verticalId,
        showVerticalDropdown: false,
        type: beerData.length > 0 && 'no-padding',
        projectName: 'Craft beer',
      },
      location: props,
      sectionId: 'contact',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Craft Beer & Brewery',
          breadcrumbLink: '#',
        },
        {
          breadcrumbName: 'Contact Us',
          breadcrumbLink: '#',
        },
      ],
      theme: 'light-theme',
    },
    {
      component: FooterSection,
      data: { siteSettings, footerLink },
      theme: 'dark-theme',
      sectionId: 'te-links',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Craft Beer & Brewery',
          breadcrumbLink: '#',
        },
        {
          breadcrumbName: 'Links',
          breadcrumbLink: '#',
        },
      ],
    },
  ];

  return (
    <>
      <PageTemplate
        theme={theme}
        componentList={componentList}
        updateTheme={e => setTheme(e)}
        verticals={verticals}
        salesEmail={contact.email}
        siteSettings={siteSettings}
        headerButtons={
          <HeaderButtons
            shareTheme={'dark'}
            getInTouchTheme={'light'}
            setIsModalOpen={setIsModalOpen}
            setIsShareModalOpen={setIsShareModalOpen}
          />
        }
      />
      <GetInTouchModal
        isOpen={isModalOpen}
        onClosePress={closeModal}
        // campaignCode={CampaignCode}
        salesEmail={contact.email}
        projectInterested={'Craft beer'}
      />
      <ShareModal isOpen={isShareModalOpen} onClosePress={closeShareModal} />
    </>
  );
};

const mapStateToProps = (state: AppState, props: any) => {
  const verticalKey = NavigationUtil.getVerticalKeyForLocation(props.location);

  return {
    isLoading:
      HomeSelector.isLoading(state) ||
      FooterSelector.isLoading(state) ||
      CmsPagesSelector.isCmsPageLoading(state) ||
      RestaurantSelector.getBeerLoading(state),
    footerLink: FooterSelector.getFooterData(state),
    craftBeerSectionData: CmsPagesSelector.getCmsPages(
      state,
      'Craft Beer & Brewery',
      false
    ),
    craftBeerBanner: HomeSelector.getBanners(state, false, '', 'Craft beer'),
    contact: HomeSelector.getContactForMenuItems(state, 'Craft beer'),
    verticalId: HomeSelector.getVerticalIdForKey(verticalKey),
    verticals: HomeSelector.getVerticals(state),
    siteSettings: HomeSelector.getSiteSettings(state),
    beerData: RestaurantSelector.getBeer(state),
  };
};

const mapDispatchToProps = dispatch => {
  const {
    getBannerData,
    getContactDetails,
    getVerticals,
    getSiteSettings,
  } = HomeActions;
  const { getFooterLinkData } = FooterActions;
  const { getPageData } = CmsPagesActions;
  const { getBeers } = RestaurantActions;
  return bindActionCreators(
    {
      getBannerData,
      getContactDetails,
      getVerticals,
      getPageData,
      getSiteSettings,
      getFooterLinkData,
      getBeers,
    },
    dispatch
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(CraftBeerPage);
